import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";
import { toast } from "sonner";
import {
  Sparkles, Users, Building2, CalendarDays, Zap, ExternalLink,
  ArrowRight, Compass, TrendingUp, RefreshCw,
} from "lucide-react";

const URGENCY_TONE = {
  critical: "bg-red-50 text-red-700 border-red-200",
  high: "bg-amber-50 text-amber-700 border-amber-200",
  medium: "bg-[#F1EFE9] text-[#5B5B62] border-[#E4E1D8]",
  low: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function UrgencyBadge({ urgency }) {
  if (!urgency) return null;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.14em] border rounded-sm ${URGENCY_TONE[urgency] || URGENCY_TONE.medium}`}>
      {urgency}
    </span>
  );
}

function ItemCard({ item, icon: Icon, kindLabel, testId }) {
  return (
    <div className="border border-[#E4E1D8] bg-white rounded-md p-4 hover:shadow-sm transition-shadow" data-testid={testId}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-[#0B2545]" strokeWidth={1.5} />
          <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-[#5B5B62]">{kindLabel}</span>
        </div>
        <UrgencyBadge urgency={item.urgency} />
      </div>
      <div className="text-sm font-medium text-[#1F2937] leading-snug mb-1">{item.name}</div>
      {item.description && <div className="text-xs text-[#5B5B62] line-clamp-3">{item.description}</div>}
      {item.starts_at && <div className="mt-2 text-[11px] font-mono text-[#8B8B8F]">📅 {item.starts_at}</div>}
      {item.url && (
        <a href={item.url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-[#0B2545] hover:underline">
          <ExternalLink className="h-3 w-3" /> Voir
        </a>
      )}
    </div>
  );
}

export default function Today() {
  const [board, setBoard] = useState(null);
  const [recos, setRecos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRecos, setLoadingRecos] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const b = await api.get("/network/today");
      setBoard(b.data);
    } catch (e) {
      const detail = e.response?.data?.detail || "Chargement impossible";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const loadRecos = async () => {
    setLoadingRecos(true);
    try {
      const r = await api.get("/network/recommendations/for-me?limit=6");
      setRecos(r.data.items || []);
    } catch {
      // silent — recos are best-effort
    } finally {
      setLoadingRecos(false);
    }
  };

  useEffect(() => {
    loadAll();
    loadRecos();
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    await Promise.all([loadAll(), loadRecos()]);
    setRefreshing(false);
    toast.success("Tableau actualisé");
  };

  const totalItems = board
    ? board.counts.opportunities + board.counts.events + board.counts.organizations + board.counts.persons
    : 0;

  return (
    <div className="min-h-screen bg-[#F8F7F3]" data-testid="today-page">
      <TopBar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* Header */}
        <div className="flex items-start justify-between mb-10 gap-6 flex-wrap">
          <div className="max-w-3xl">
            <div className="text-xs font-mono uppercase tracking-[0.28em] text-navy mb-3">Network OS · Kompas quotidien</div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-navy leading-[1.05]">Opportunités du jour</h1>
            <p className="mt-5 text-base md:text-lg text-[#374151] leading-relaxed max-w-2xl">
              Que puis-je manquer aujourd&apos;hui&nbsp;? Un tableau agrégé des personnes, organisations,
              événements et opportunités les plus stratégiques pour votre trajectoire.
            </p>
          </div>
          <button
            data-testid="today-refresh-btn"
            onClick={refresh}
            disabled={refreshing}
            className="inline-flex items-center gap-2 border-2 border-[#E5E7EB] px-4 py-2.5 text-sm font-medium text-[#374151] hover:border-navy hover:text-navy rounded-md disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} strokeWidth={2} />
            Actualiser
          </button>
        </div>

        {/* AI Recommendations block (killer) */}
        <section className="mb-10" data-testid="today-recos-section">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-[#C5A880]" strokeWidth={1.5} />
            <h2 className="text-sm font-mono uppercase tracking-[0.18em] text-[#5B5B62]">Recommandations IA — top {recos.length || 6}</h2>
          </div>
          {loadingRecos ? (
            <div className="text-sm text-[#5B5B62]">Analyse en cours (Sonnet 4.6)…</div>
          ) : recos.length === 0 ? (
            <div className="text-sm text-[#5B5B62]">Aucune recommandation pour le moment.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {recos.map((r) => (
                <div key={r.id} className="border border-[#E7D9B3] bg-[#FBF6EB] rounded-md p-4" data-testid={`today-reco-${r.id}`}>
                  <div className="flex items-start justify-between mb-2 gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-[#7A5B14]">{r.type}</span>
                      {r.score != null && <span className="text-[10px] font-mono text-[#7A5B14]">score {Math.round(r.score)}</span>}
                    </div>
                    <UrgencyBadge urgency={r.urgency} />
                  </div>
                  <div className="text-sm font-medium text-[#1F2937] mb-2 leading-snug">{r.name}</div>
                  {r.why_fr && <div className="text-xs text-[#5B5B62] mb-2"><b className="text-[#0B2545]">Pourquoi&nbsp;:</b> {r.why_fr}</div>}
                  {r.why_now_fr && <div className="text-xs text-[#5B5B62] mb-2"><b className="text-[#0B2545]">Pourquoi maintenant&nbsp;:</b> {r.why_now_fr}</div>}
                  {r.concrete_action_fr && <div className="text-xs text-[#5B5B62] mb-2"><b className="text-[#0B2545]">Action&nbsp;:</b> {r.concrete_action_fr}</div>}
                  {r.potential_impact_fr && <div className="text-xs text-[#5B5B62]"><b className="text-[#0B2545]">Impact&nbsp;:</b> {r.potential_impact_fr}</div>}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Overview stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <StatBox label="Opportunités" value={board?.counts.opportunities || 0} icon={Zap} testId="today-stat-opps" />
          <StatBox label="Événements" value={board?.counts.events || 0} icon={CalendarDays} testId="today-stat-events" />
          <StatBox label="Organisations" value={board?.counts.organizations || 0} icon={Building2} testId="today-stat-orgs" />
          <StatBox label="Personnes" value={board?.counts.persons || 0} icon={Users} testId="today-stat-persons" />
        </div>

        {/* Sections */}
        {loading ? (
          <div className="text-sm text-[#5B5B62]">Chargement…</div>
        ) : totalItems === 0 ? (
          <div className="border border-dashed border-[#E4E1D8] rounded-md p-8 text-center text-sm text-[#5B5B62]">
            L&apos;écosystème est encore silencieux — revenez plus tard.
          </div>
        ) : (
          <div className="space-y-8">
            {board.events.length > 0 && (
              <Section title="Événements à venir" icon={CalendarDays} items={board.events} kindLabel="event" testId="today-events" />
            )}
            {board.organizations.length > 0 && (
              <Section title="Organisations à connaître" icon={Building2} items={board.organizations} kindLabel="organization" testId="today-orgs" />
            )}
            {board.persons.length > 0 && (
              <Section title="Personnes clés à approcher" icon={Users} items={board.persons} kindLabel="person" testId="today-persons" />
            )}
            {board.opportunities.length > 0 && (
              <Section title="Opportunités cachées (HOE™)" icon={Compass} items={board.opportunities} kindLabel="opportunity" testId="today-opps" />
            )}
          </div>
        )}

        {/* CTA */}
        <div className="mt-10 border-t border-[#E4E1D8] pt-6 flex items-center justify-between">
          <div className="text-sm text-[#5B5B62]">Explorez plus loin&nbsp;:</div>
          <div className="flex gap-2">
            <Link to="/app/network" className="inline-flex items-center gap-1 border border-[#E4E1D8] px-3 py-2 text-sm text-[#1F2937] hover:bg-white rounded-md" data-testid="today-cta-network">
              <TrendingUp className="h-4 w-4" strokeWidth={1.5} /> Écosystème
              <ArrowRight className="h-3 w-3" />
            </Link>
            <Link to="/app/communities" className="inline-flex items-center gap-1 border border-[#E4E1D8] px-3 py-2 text-sm text-[#1F2937] hover:bg-white rounded-md" data-testid="today-cta-communities">
              <Users className="h-4 w-4" strokeWidth={1.5} /> Communautés
              <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, icon: Icon, testId }) {
  return (
    <div className="border border-[#E4E1D8] bg-white rounded-md p-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[#5B5B62]">{label}</div>
        <Icon className="h-4 w-4 text-[#0B2545]" strokeWidth={1.5} />
      </div>
      <div className="mt-2 text-2xl font-display text-[#0B2545]">{value}</div>
    </div>
  );
}

function Section({ title, icon: Icon, items, kindLabel, testId }) {
  return (
    <section data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-[#0B2545]" strokeWidth={1.5} />
        <h2 className="text-sm font-mono uppercase tracking-[0.18em] text-[#5B5B62]">{title}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {items.map((it) => <ItemCard key={it.id} item={it} icon={Icon} kindLabel={kindLabel} testId={`${testId}-${it.id}`} />)}
      </div>
    </section>
  );
}
